import { useState, useCallback, useEffect } from 'react';
import { combinationRules } from '../data/regionalChef/combinationRules';
import { recipesData } from '../data/regionalChef/recipesData';
import { calculateCombineAP, calculateServePrice } from '../utils/buffEngine';
import { GAME_SETTINGS } from '../data/regionalChef/gameConfig';

const STAGE_MODIFIERS = [
  { id: 'normal', name: 'วันปกติ', desc: 'ไม่มีเงื่อนไขพิเศษ บริหารร้านตามสบาย', color: '#2ed573' },
  { id: 'forestry_law', name: 'สัมปทานป่าไม้', desc: 'ฟืนขาดแคลน! การ [ต้ม] และ [ผัด] ใช้ AP เพิ่มขึ้น 1 หน่วย', color: '#eccc68' },
  { id: 'rush_hour', name: 'ชั่วโมงเร่งด่วน', desc: 'ลูกค้าเยอะแต่เวลาน้อย AP เริ่มต้นลดลง 2 หน่วย', color: '#ff4757' },
  { id: 'vegetarian_fest', name: 'วันพระใหญ่', desc: 'งดเนื้อสัตว์! เมนูหมู กุ้ง และไก่ ราคาตก 50%', color: '#ff7f50' }
];

// 🌟 เด็คเริ่มต้นแจกฟรีสำหรับโหมดเด็กประถม
const KIDS_STARTER_DECK = [
  'raw_pork', 'raw_pork', 'raw_chicken', 'raw_shrimp', 'raw_egg', 'raw_egg',
  'raw_papaya', 'raw_chili', 'raw_chili', 'raw_garlic', 'raw_garlic',
  'raw_curry_herbs', 'raw_curry_herbs', 'raw_tomato', 'raw_sato', 'raw_noodles'
];

export const useChefGameLogic = () => {
  const { initialAP, initialTarget, maxBuffSlots, initialMoney } = GAME_SETTINGS;

  const [isKidsMode, setIsKidsMode] = useState(false); // 👈 State เช็กโหมดเด็ก

  const [day, setDay] = useState(1);
  const [ap, setAp] = useState(initialAP);
  const [money, setMoney] = useState(0); 
  const [dailySales, setDailySales] = useState(0); 
  const [dailyTarget, setDailyTarget] = useState(initialTarget); 
  const [gameState, setGameState] = useState('idle'); 
  const [masterDeck, setMasterDeck] = useState([]); 
  const [deck, setDeck] = useState([]); 
  const [inventory, setInventory] = useState([]); 
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [activeBuffs, setActiveBuffs] = useState([]); 
  const [recipeLevels, setRecipeLevels] = useState({}); 
  const [unlockedRecipes, setUnlockedRecipes] = useState([]); 
  const [currentModifier, setCurrentModifier] = useState(STAGE_MODIFIERS[0]); 
  const [showPopup, setShowPopup] = useState(null); 
  const [showRecipeBook, setShowRecipeBook] = useState(false); 
  const [bonusSummary, setBonusSummary] = useState(null); 

  // ⚠️ ปรับปรุง initGame ให้รองรับ Mode
  const initGame = useCallback((mode = 'normal') => {
    const isKids = mode === 'kids';
    setIsKidsMode(isKids);
    setRecipeLevels({});
    setActiveBuffs([]);
    setBonusSummary(null);
    setCurrentModifier(STAGE_MODIFIERS[0]);
    setSelectedIndices([]);

    if (isKids) {
      // โหมดเด็กประถม: เฉลยสูตรหมด จัดเด็คให้ แจกไพ่ 6 ใบ และข้าม Day 0
      setUnlockedRecipes(Object.keys(recipesData)); 
      setMoney(0);
      setDailySales(0);
      setDailyTarget(initialTarget);
      setDay(1);

      const initialDeck = [...KIDS_STARTER_DECK].sort(() => Math.random() - 0.5);
      const initialHand = initialDeck.splice(0, 6); // มือถือไพ่ 6 ใบ
      
      setMasterDeck(KIDS_STARTER_DECK);
      setDeck(initialDeck);
      setInventory(initialHand);
      
      // AP ล้นๆ เพราะได้โบนัสจากการปลดล็อกสูตรทั้งหมดแล้ว
      const bonusAP = Math.floor(Object.keys(recipesData).length / 2); 
      setAp(initialAP + bonusAP);
      
      setGameState('playing'); // ข้ามไปเล่นเลย
    } else {
      // โหมดปกติ
      setUnlockedRecipes([]);
      setMasterDeck([]);
      setDeck([]);
      setInventory([]);
      setMoney(initialMoney); 
      setDailySales(0); 
      setDailyTarget(initialTarget); 
      setDay(1);
      setGameState('prep_shop'); 
    }
  }, [initialMoney, initialTarget]);

  const startNextDay = useCallback((isInitial = false) => {
    const requiredCards = isKidsMode ? 6 : 4; // 👈 ขั้นต่ำเปลี่ยนตามโหมด
    if (masterDeck.length < requiredCards) return;

    const shuffledDeck = [...masterDeck].sort(() => Math.random() - 0.5);
    const initialHand = shuffledDeck.splice(0, requiredCards); // 👈 จั่ว 6 ใบถ้าเป็นโหมดเด็ก
    
    let nextDay = isInitial ? 1 : day + 1;
    
    const bonusAP = Math.floor(unlockedRecipes.length / 2);
    let startingAP = initialAP + bonusAP;

    if (currentModifier.id === 'rush_hour') startingAP = Math.max(1, startingAP - 2);
    
    setInventory(initialHand);
    setDeck(shuffledDeck);
    setDay(nextDay);
    
    setDailyTarget(prev => isInitial ? initialTarget : Math.floor(prev * 1.5)); 
    setDailySales(0); 
    
    setAp(startingAP);
    setSelectedIndices([]); 
    setBonusSummary(null);
    setGameState('playing');
  }, [masterDeck, day, initialAP, initialTarget, unlockedRecipes.length, currentModifier, isKidsMode]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    if (dailySales >= dailyTarget) {
      const apBonus = ap * 10; 
      const leftoverCards = [...inventory, ...deck].filter(card => card !== null);
      const totalBonus = apBonus; 

      setMoney(prev => prev + totalBonus); 
      setBonusSummary({ apBonus, leftoverCardsCount: leftoverCards.length, totalBonus });

      const nextMod = STAGE_MODIFIERS[Math.floor(Math.random() * STAGE_MODIFIERS.length)];
      setCurrentModifier(nextMod);

      setMasterDeck(leftoverCards);
      setInventory([]);
      setDeck([]);

      setGameState('shop'); 
    } else if (ap <= 0) {
      setGameState('game_over');
    }
  }, [dailySales, dailyTarget, ap, gameState, inventory, deck]);

  const toggleCardSelection = useCallback((indexInHand) => {
    setSelectedIndices((prev) => {
      if (prev.includes(indexInHand)) return prev.filter(i => i !== indexInHand);
      if (prev.length < 3) return [...prev, indexInHand];
      return prev;
    });
  }, []);

  const performAction = useCallback((actionId) => {
    if (selectedIndices.length === 0 || ap <= 0) return;

    const selectedIngredientIds = selectedIndices.map(index => inventory[index]);
    const attemptedCombination = [...selectedIngredientIds, actionId];

    const matchedRule = combinationRules.find(rule => {
      if (rule.ingredients.length !== attemptedCombination.length) return false;
      let tempRuleIngredients = [...rule.ingredients];
      for (let item of attemptedCombination) {
        const index = tempRuleIngredients.indexOf(item);
        if (index > -1) tempRuleIngredients.splice(index, 1);
        else return false;
      }
      return true;
    });

    if (matchedRule) {
      const apCost = calculateCombineAP(1, actionId, activeBuffs, currentModifier);
      if (ap >= apCost) {
        setAp(prev => prev - apCost);
        const newInventory = [...inventory];
        const newDeck = [...deck];

        newInventory[selectedIndices[0]] = matchedRule.result;
        for (let i = 1; i < selectedIndices.length; i++) {
          const targetIndex = selectedIndices[i];
          if (newDeck.length > 0) {
            newInventory[targetIndex] = newDeck.shift(); 
          } else {
            newInventory[targetIndex] = null; 
          }
        }

        setInventory(newInventory.filter(card => card !== null));
        setDeck(newDeck);
        setSelectedIndices([]);

        if (matchedRule.result.startsWith('recipe_') && !unlockedRecipes.includes(matchedRule.result)) {
          setUnlockedRecipes(prev => [...prev, matchedRule.result]);
          setShowPopup(recipesData[matchedRule.result]);
        }
      }
    } else {
      setSelectedIndices([]); 
    }
  }, [selectedIndices, inventory, ap, activeBuffs, currentModifier, unlockedRecipes, deck]);

  const serveFood = useCallback((foodCardIndex) => {
    const foodId = inventory[foodCardIndex];
    const recipeInfo = recipesData[foodId];

    if (recipeInfo && ap > 0) {
      setAp(prev => prev - 1); 
      const recipeLevel = recipeLevels[foodId] || 1;
      const finalPrice = calculateServePrice(recipeInfo.basePrice, recipeInfo, activeBuffs, recipeLevel, currentModifier);
      
      setMoney(prev => prev + finalPrice); 
      setDailySales(prev => prev + finalPrice); 
      
      const newInventory = [...inventory];
      const newDeck = [...deck];

      if (newDeck.length > 0) {
        newInventory[foodCardIndex] = newDeck.shift(); 
      } else {
        newInventory[foodCardIndex] = null; 
      }

      setInventory(newInventory.filter(card => card !== null));
      setDeck(newDeck);
      if (selectedIndices.includes(foodCardIndex)) setSelectedIndices([]);
    }
  }, [ap, inventory, activeBuffs, selectedIndices, deck, recipeLevels, currentModifier]);

  const discardAndDraw = useCallback(() => {
    if (selectedIndices.length === 0 || ap <= 0 || deck.length === 0) return;
    setAp(prev => prev - 1);
    const newDeck = [...deck];
    const newInventory = [...inventory];

    selectedIndices.forEach(index => {
      const discardedCard = newInventory[index];
      if (newDeck.length > 0) {
        newDeck.push(discardedCard); 
        newInventory[index] = newDeck.shift();
      }
    });

    setDeck(newDeck); 
    setInventory(newInventory);
    setSelectedIndices([]); 
  }, [selectedIndices, inventory, deck, ap]);

  const buyIngredient = useCallback((ingredientId, cost) => {
    if (money >= cost) {
      setMoney(prev => prev - cost);
      setMasterDeck(prev => [...prev, ingredientId]);
      return true;
    }
    return false;
  }, [money]);

  const buyBuff = useCallback((buffId, cost) => {
    if (money >= cost && activeBuffs.length < maxBuffSlots) {
      setMoney(prev => prev - cost);
      setActiveBuffs(prev => [...prev, buffId]);
      return true;
    }
    return false;
  }, [money, activeBuffs.length, maxBuffSlots]);

  const upgradeRecipe = useCallback((recipeId, cost) => {
    if (money >= cost) {
      setMoney(prev => prev - cost);
      setRecipeLevels(prev => ({ ...prev, [recipeId]: (prev[recipeId] || 1) + 1 }));
      return true;
    }
    return false;
  }, [money]);

  const closePopup = () => setShowPopup(null);
  const toggleRecipeBook = () => setShowRecipeBook(!showRecipeBook);

  return {
    day, ap, money, dailySales, dailyTarget, inventory, deck, masterDeck, selectedIndices, gameState, 
    showPopup, activeBuffs, maxBuffSlots, bonusSummary, isKidsMode, // 👈 ส่ง isKidsMode ออกไปใช้
    unlockedRecipes, recipeLevels, currentModifier, showRecipeBook,
    initGame,
    toggleCardSelection, performAction, serveFood, discardAndDraw, 
    buyIngredient, buyBuff, upgradeRecipe, startNextDay, closePopup, toggleRecipeBook
  };
};